/**
 * Zielfelder des CSV-Imports und das Raten des Mappings.
 *
 * Steht bewusst hier und nicht in `services/import.ts`: die Oberfläche braucht
 * `guessMapping` im Schritt „Datei“, und ein Wertimport aus `@epasu/core` zöge
 * die Datenbank samt better-sqlite3 in den Browser-Bundle. Dieses Modul ist
 * frei von Abhängigkeiten und über `@epasu/core/mapping` erreichbar.
 */

/**
 * Zielfelder, auf die CSV-Spalten gemappt werden können.
 *
 * Die `organization*`-Felder gehen an die Firma, der Rest an den Kontakt. Eine
 * Leadliste beschreibt oft nur Firmen — Name, Branche, Gebiet, Website — und
 * hat als einzige Person ein `info@`. Ohne Firmenfelder fiele davon fast alles
 * beim Import unter den Tisch.
 */
export type ContactField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'role'
  | 'salutation'
  | 'formOfAddress'
  | 'personalSalutation'
  | 'organizationName'
  | 'organizationIndustry'
  | 'organizationRegion'
  | 'organizationWebsite'
  | 'organizationEmail'
  | 'organizationPhone'
  | 'organizationStreet'
  | 'organizationPostalCode'
  | 'organizationCity'
  | 'organizationNotes';

/** Mapping: Zielfeld → CSV-Spaltenname. */
export type ColumnMapping = Partial<Record<ContactField, string>>;

/**
 * Erkennungsmuster, in dieser Reihenfolge geprüft: das Erste, das passt,
 * gewinnt. Die eindeutigen Überschriften stehen deshalb vorn — „Vorname“
 * enthält auch „name“, darf aber nie als Nachname durchgehen. Ebenso
 * „Anredeform“, „Anrede_Titel“ und „Anrede_Person“: sie enthalten „anrede“,
 * sind aber Herr/Frau bzw. die persönliche Zeile, nicht die Team-Anrede.
 */
const MUSTER: [ContactField, RegExp][] = [
  ['personalSalutation', /anrede.?person|pers(ö|oe)nliche.?anrede/],
  ['formOfAddress', /anrede.?form|anrede.?titel|geschlecht|herr.?\/.?frau|gender/],
  ['salutation', /anrede|salutation|begr(ü|ue)(ß|ss)ung/],
  ['organizationEmail', /firmen.?e-?mail|firmenmail|zentrale/],
  ['email', /mail/],
  ['firstName', /vorname|first ?name|given/],
  ['lastName', /nachname|last ?name|surname|familienname/],
  ['organizationWebsite', /website|webseite|homepage|^url$|internet/],
  ['organizationIndustry', /branche|typ|kategorie|sparte|industry/],
  ['organizationRegion', /gebiet|region|einsatz|umkreis|bundesland/],
  ['organizationPostalCode', /plz|postleitzahl|postal|zip/],
  ['organizationStreet', /stra(ß|ss)e|anschrift|adresse|street/],
  ['organizationCity', /^ort$|stadt|city|sitz/],
  ['organizationNotes', /notiz|warum|bemerkung|kommentar|hinweis|note/],
  ['organizationName', /firma|unternehmen|organisation|schule|betrieb|company/],
  ['role', /rolle|role|funktion|position/],
  ['phone', /tel|phone|fon|mobil/],
];

/**
 * Rät aus den Spaltenüberschriften ein Mapping.
 *
 * Ein blankes „Name“ ging bisher an den Nachnamen — bei einer Firmenliste stand
 * der Firmenname damit als Nachname einer namenlosen Person. Geraten bleibt
 * geraten: der Mapping-Schritt zeigt das Ergebnis zur Korrektur.
 */
export function guessMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};

  for (const header of headers) {
    const low = header.toLowerCase().trim();
    const treffer = MUSTER.find(([, re]) => re.test(low));
    if (treffer && !mapping[treffer[0]]) mapping[treffer[0]] = header;
  }

  // „Name“ allein trifft kein Muster. Steht ein Vorname daneben, ist der
  // Nachname gemeint; sonst die Firma.
  const blank = headers.find((h) => /^name$/i.test(h.trim()));
  if (blank) {
    if (mapping.firstName && !mapping.lastName) mapping.lastName = blank;
    else if (!mapping.organizationName) mapping.organizationName = blank;
  }

  // Keine Personenspalte, aber ein Firmenname: eine Firmenliste. Ein blankes
  // „Telefon“ ist dann die Nummer der Firma, nicht die der Person hinter info@.
  const firmenliste = !mapping.firstName && !mapping.lastName && !!mapping.organizationName;
  if (firmenliste && mapping.phone && !mapping.organizationPhone) {
    mapping.organizationPhone = mapping.phone;
    delete mapping.phone;
  }

  // Die Adresse bleibt zusätzlich am Kontakt: sie ist der Schlüssel für
  // Dubletten und dafür, wem eingehende Post gehört. Anders als beim Telefon
  // wird sie also kopiert, nicht verschoben.
  if (firmenliste && mapping.email && !mapping.organizationEmail) {
    mapping.organizationEmail = mapping.email;
  }

  return mapping;
}
